import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

// Styled Components
const MenuContainer = styled.div`
  background-color: #0a0a0a;
  border-radius: 12px;
  padding: 24px;
  color: white;
  min-height: 600px;
  max-height: 80vh;
  overflow-y: auto;
`;

const MenuHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const RestaurantName = styled.h2`
  color: #D4AF37;
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: bold;
`;

const RestaurantDescription = styled.p`
  color: #ccc;
  margin: 0;
  font-size: 16px;
`;

const MenuCategories = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  overflow-x: auto;
  padding-bottom: 8px;
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #1a1a1a;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #D4AF37;
    border-radius: 3px;
  }
`;

const CategoryTab = styled.button`
  padding: 10px 20px;
  background-color: ${props => props.$active ? '#D4AF37' : 'transparent'};
  color: ${props => props.$active ? '#0a0a0a' : '#D4AF37'};
  border: 2px solid #D4AF37;
  border-radius: 25px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: ${props => props.$active ? 'bold' : '500'};
  white-space: nowrap;
  
  &:hover {
    background-color: ${props => props.$active ? '#B8860B' : 'rgba(212, 175, 55, 0.1)'};
    transform: translateY(-1px);
  }
`;

const MenuItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

const MenuItemCard = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%);
  border: 2px solid #374151;
  border-radius: 16px;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    border-color: #D4AF37;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #D4AF37, #fbbf24);
    opacity: 0.3;
  }
`;

const ItemImage = styled.div`
  width: 100%;
  height: 160px;
  background: linear-gradient(135deg, #374151 0%, #4b5563 100%);
  border-radius: 12px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: #9ca3af;
`;

const ItemName = styled.h3`
  color: #D4AF37;
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: bold;
`;

const ItemDescription = styled.p`
  color: #d1d5db;
  margin: 0 0 12px 0;
  font-size: 14px;
  line-height: 1.4;
  min-height: 40px;
`;

const ItemPrice = styled.div`
  color: #22c55e;
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 16px;
`;

const ItemActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #1f2937;
  border-radius: 20px;
  padding: 4px;
`;

const QuantityButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: #D4AF37;
  color: #0a0a0a;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: #fbbf24;
    transform: scale(1.1);
  }
  
  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
    transform: none;
  }
`;

const QuantityDisplay = styled.span`
  color: white;
  font-weight: 500;
  min-width: 20px;
  text-align: center;
`;

const AddToCartButton = styled.button`
  flex: 1;
  padding: 10px 16px;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: white;
  border: none;
  border-radius: 20px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(34, 197, 94, 0.3);
  }
  
  &:disabled {
    background: #6b7280;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const LoadingSpinner = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #D4AF37;
  font-size: 18px;
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyStateIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyStateText = styled.p`
  color: #9ca3af;
  font-size: 16px;
  line-height: 1.5;
  max-width: 400px;
  margin: 0 auto;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

const CloseButton = styled.button`
  padding: 12px 24px;
  background: transparent;
  color: #9ca3af;
  border: 2px solid #4b5563;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #6b7280;
    color: #d1d5db;
  }
`;

const ViewCartButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #D4AF37 0%, #fbbf24 100%);
  color: #0a0a0a;
  border: none;
  border-radius: 25px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(212, 175, 55, 0.3);
  }
`;

const RestaurantMenu = ({ restaurant, onOrder, onClose, onAddToCart }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemQuantities, setItemQuantities] = useState({});

  // Mock menu categories
  const categories = [
    { id: 'all', name: 'All Items', emoji: '🍽️' },
    { id: 'appetizers', name: 'Appetizers', emoji: '🥨' },
    { id: 'main', name: 'Main Dishes', emoji: '🍔' },
    { id: 'drinks', name: 'Drinks', emoji: '🥤' },
    { id: 'desserts', name: 'Desserts', emoji: '🍰' }
  ];

  useEffect(() => {
    if (restaurant) {
      loadRestaurantMenu();
    }
  }, [restaurant]);

  const loadRestaurantMenu = async () => {
    try {
      setLoading(true);
      console.log('🍽️ Loading menu for:', restaurant.businessName);
      
      // This would call your Codebase 1 API to get the restaurant's menu
      // For now, using mock data that matches the expected format
      const mockMenuItems = [
        {
          id: 'item_001',
          name: 'Classic Burger',
          description: 'Juicy beef patty with lettuce, tomato, and special sauce',
          price: 12.99,
          category: 'main',
          image: '🍔',
          available: true
        },
        {
          id: 'item_002',
          name: 'Loaded Fries',
          description: 'Crispy fries topped with cheese, bacon, and green onions',
          price: 8.99,
          category: 'appetizers',
          image: '🍟',
          available: true
        },
        {
          id: 'item_003',
          name: 'Fresh Smoothie',
          description: 'Blend of strawberries, bananas, and orange juice',
          price: 6.99,
          category: 'drinks',
          image: '🥤',
          available: true
        },
        {
          id: 'item_004',
          name: 'Chocolate Cake',
          description: 'Rich chocolate cake with vanilla frosting',
          price: 7.99,
          category: 'desserts',
          image: '🍰',
          available: true
        },
        {
          id: 'item_005',
          name: 'Fish Tacos',
          description: 'Grilled fish with cabbage slaw and chipotle mayo',
          price: 14.99,
          category: 'main',
          image: '🌮',
          available: true
        },
        {
          id: 'item_006',
          name: 'Onion Rings',
          description: 'Crispy beer-battered onion rings with ranch dipping sauce',
          price: 6.99,
          category: 'appetizers',
          image: '🧅',
          available: true
        }
      ];
      
      setMenuItems(mockMenuItems);
      
      // Initialize quantities
      const initialQuantities = {};
      mockMenuItems.forEach(item => {
        initialQuantities[item.id] = 1;
      });
      setItemQuantities(initialQuantities);
      
      console.log('✅ Menu loaded:', mockMenuItems.length, 'items');
      
    } catch (error) {
      console.error('❌ Failed to load menu:', error);
      setMenuItems([]);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredMenuItems = () => {
    if (selectedCategory === 'all') {
      return menuItems.filter(item => item.available);
    }
    return menuItems.filter(item => 
      item.available && item.category === selectedCategory
    );
  };

  const handleQuantityChange = (itemId, change) => {
    const currentQuantity = itemQuantities[itemId] || 1;
    const newQuantity = Math.max(1, currentQuantity + change);
    
    setItemQuantities(prev => ({
      ...prev,
      [itemId]: newQuantity
    }));
  };

  const handleAddToCart = (item) => {
    const quantity = itemQuantities[item.id] || 1;
    const cartItem = {
      ...item,
      quantity,
      restaurantId: restaurant.merchantId,
      restaurantName: restaurant.businessName
    };
    
    console.log('🛒 Adding to cart:', cartItem);
    onAddToCart(cartItem);
    
    // Reset quantity to 1
    setItemQuantities(prev => ({
      ...prev,
      [item.id]: 1
    }));
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const filteredMenuItems = getFilteredMenuItems();

  return (
    <MenuContainer>
      <MenuHeader>
        <RestaurantName>{restaurant.businessName}</RestaurantName>
        <RestaurantDescription>
          {restaurant.description || 'Delicious food and drinks available now!'}
        </RestaurantDescription>
      </MenuHeader>

      <MenuCategories>
        {categories.map(category => (
          <CategoryTab
            key={category.id}
            $active={selectedCategory === category.id}
            onClick={() => setSelectedCategory(category.id)}
          >
            {category.emoji} {category.name}
          </CategoryTab>
        ))}
      </MenuCategories>

      {loading ? (
        <LoadingSpinner>
          🔄 Loading menu items...
        </LoadingSpinner>
      ) : filteredMenuItems.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>🍽️</EmptyStateIcon>
          <EmptyStateText>
            {selectedCategory === 'all' 
              ? 'No menu items are currently available.'
              : `No ${categories.find(c => c.id === selectedCategory)?.name.toLowerCase()} are currently available.`
            }
          </EmptyStateText>
        </EmptyState>
      ) : (
        <MenuItemsGrid>
          {filteredMenuItems.map(item => (
            <MenuItemCard key={item.id}>
              <ItemImage>{item.image}</ItemImage>
              
              <ItemName>{item.name}</ItemName>
              
              <ItemDescription>{item.description}</ItemDescription>
              
              <ItemPrice>{formatPrice(item.price)}</ItemPrice>
              
              <ItemActions>
                <QuantityControl>
                  <QuantityButton
                    onClick={() => handleQuantityChange(item.id, -1)}
                    disabled={itemQuantities[item.id] <= 1}
                  >
                    -
                  </QuantityButton>
                  <QuantityDisplay>{itemQuantities[item.id] || 1}</QuantityDisplay>
                  <QuantityButton
                    onClick={() => handleQuantityChange(item.id, 1)}
                  >
                    +
                  </QuantityButton>
                </QuantityControl>
                
                <AddToCartButton
                  onClick={() => handleAddToCart(item)}
                  disabled={!item.available}
                >
                  {item.available ? 'Add to Cart' : 'Unavailable'}
                </AddToCartButton>
              </ItemActions>
            </MenuItemCard>
          ))}
        </MenuItemsGrid>
      )}

      <ActionButtons>
        <CloseButton onClick={onClose}>
          Back to Marketplace
        </CloseButton>
        <ViewCartButton onClick={() => onOrder({ action: 'view_cart' })}>
          🛒 View Cart
        </ViewCartButton>
      </ActionButtons>
    </MenuContainer>
  );
};

export default RestaurantMenu;
